import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import FilterBar from './components/FilterBar';
import UnifiedDataTable from './components/UnifiedDataTable';
import FlagModal from './components/FlagModal';
import UnifiedUploadModal from '../../components/UnifiedUploadModal';
import EmptyState from './components/EmptyState';
import { getRotations, getTimecards } from '../../services/apiService';

const DataManagement = () => {
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    status: 'all',
    sortBy: 'date-desc',
    documentType: 'all' // all, rotation, timecard
  });
  const [flagModalOpen, setFlagModalOpen] = useState(false);
  const [selectedRotation, setSelectedRotation] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteStep, setDeleteStep] = useState(1); // 1: confirm, 2: are you sure, 3: final yes
  const [itemToDelete, setItemToDelete] = useState(null);
  const [flagEditModalOpen, setFlagEditModalOpen] = useState(false);
  const [selectedFlag, setSelectedFlag] = useState(null);

  useEffect(() => {
    loadAllData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [allData, filters]);

  const loadAllData = async () => {
    try {
      setIsLoading(true);
      
      // ✅ PERFORMANCE FIX: Fetch rotations and timecards in parallel
      // Link counts are now included in each response - no separate API call needed!
      const [rotationsResponse, timecardsResponse] = await Promise.all([
        getRotations().catch(err => {
          console.error('Failed to load rotations:', err);
          return { rotations: [] };
        }),
        getTimecards().catch(err => {
          console.error('Failed to load timecards:', err);
          return { timecards: [] };
        })
      ]);
      
      const rotationsList = rotationsResponse?.rotations || [];
      const timecardsList = timecardsResponse?.timecards || [];
      
      // Transform rotations (link_count now included in API response)
      const transformedRotations = rotationsList.map(rotation => ({
        type: 'rotation',
        id: rotation.id,
        identifier: rotation.rotation_number,
        employeeName: rotation.employee_name,
        employeeId: rotation.employee_id,
        startDate: rotation.rotation_start_date,
        endDate: rotation.rotation_end_date,
        creditHours: rotation.total_credit_hours || 0,
        totalPayHours: null, // Rotations don't have total pay hours
        flightCount: rotation.total_flights || 0,
        status: rotation.status || 'valid',
        flags: rotation.flags || [],
        linked: (rotation.link_count || 0) > 0,  // ✅ Use link_count from API
        linkedTo: null
      }));
      
      // Transform timecards (link_count now included in API response)
      const transformedTimecards = timecardsList.map(timecard => {
        const tcId = timecard.timecard_id || timecard.id;
        return {
          type: 'timecard',
          id: tcId,
          identifier: timecard.bid_period || 'Unknown Period',
          employeeName: timecard.employee_name,
          employeeId: timecard.employee_id,
          startDate: timecard.period_start_date || timecard.created_at,
          endDate: timecard.period_end_date || timecard.created_at,
          creditHours: null, // Timecards don't show credit hours
          totalPayHours: timecard.total_pay_hours || 0,
          flightCount: 0,
          status: 'valid',
          flags: [],
          linked: (timecard.link_count || 0) > 0,  // ✅ Use link_count from API
          linkedTo: null
        };
      });
      
      // Merge both arrays
      const merged = [...transformedRotations, ...transformedTimecards];
      
      setAllData(merged);
    } catch (error) {
      console.error('Failed to load data:', error);
      setAllData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...allData];

    // Filter by document type
    if (filters?.documentType !== 'all') {
      filtered = filtered?.filter(item => item.type === filters.documentType);
    }

    // Filter by date range
    if (filters?.startDate) {
      filtered = filtered?.filter(item => {
        const itemStart = new Date(item.startDate);
        const filterStart = new Date(filters.startDate);
        return itemStart >= filterStart;
      });
    }

    if (filters?.endDate) {
      filtered = filtered?.filter(item => {
        const itemEnd = new Date(item.endDate);
        const filterEnd = new Date(filters.endDate);
        return itemEnd <= filterEnd;
      });
    }

    // Filter by status
    if (filters?.status !== 'all') {
      filtered = filtered?.filter(item => item?.status === filters?.status);
    }

    // Sort
    switch (filters?.sortBy) {
      case 'date-desc':
        filtered?.sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
        break;
      case 'date-asc':
        filtered?.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
        break;
      case 'hours-desc':
        filtered?.sort((a, b) => b?.creditHours - a?.creditHours);
        break;
      case 'hours-asc':
        filtered?.sort((a, b) => a?.creditHours - b?.creditHours);
        break;
      case 'identifier-asc':
        filtered?.sort((a, b) => a?.identifier?.localeCompare(b?.identifier));
        break;
      case 'identifier-desc':
        filtered?.sort((a, b) => b?.identifier?.localeCompare(a?.identifier));
        break;
      default:
        break;
    }

    setFilteredData(filtered);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      status: 'all',
      sortBy: 'date-desc',
      documentType: 'all'
    });
  };

  const handleUploadSuccess = async () => {
    await loadAllData();
    setIsUploadModalOpen(false);
  };

  const handleFlagClick = (item) => {
    if (item.type === 'rotation') {
      setSelectedRotation(item);
      setFlagModalOpen(true);
    }
  };

  const handleFlagConfirm = async (rotation, flagId) => {
    try {
      const response = await fetch(`http://localhost:8000/api/v1/rotations/${rotation.id}/flags`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          flag_type: flagId
        })
      });

      if (response.status === 409) {
        alert(`${flagId.toUpperCase()} flag is already applied to rotation ${rotation.identifier}`);
        setFlagModalOpen(false);
        return;
      }

      if (!response.ok) {
        throw new Error(`Failed to add flag: ${response.statusText}`);
      }

      // Update local state
      setAllData(allData.map(item => {
        if (item.type === 'rotation' && item.id === rotation.id) {
          return {
            ...item,
            flags: [...(item.flags || []), flagId]
          };
        }
        return item;
      }));

      setFlagModalOpen(false);
      console.log(`✅ Added ${flagId} flag to rotation ${rotation.identifier}`);
      
    } catch (error) {
      console.error('Error adding flag:', error);
      alert(`Failed to add flag: ${error.message}`);
    }
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setDeleteStep(1);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (deleteStep < 3) {
      setDeleteStep(deleteStep + 1);
      return;
    }

    try {
      const endpoint = itemToDelete.type === 'rotation'
        ? `http://localhost:8000/api/v1/rotations/${itemToDelete.id}`
        : `http://localhost:8000/api/v1/timecards/${itemToDelete.id}`;

      const response = await fetch(endpoint, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`Failed to delete ${itemToDelete.type}`);
      }

      // Remove from local state
      setAllData(allData.filter(item => item.id !== itemToDelete.id));
      setDeleteConfirmOpen(false);
      setDeleteStep(1);
      setItemToDelete(null);
      console.log(`✅ Deleted ${itemToDelete.type} ${itemToDelete.identifier}`);
    } catch (error) {
      console.error('Error deleting:', error);
      alert(`Failed to delete: ${error.message}`);
    }
  };

  const handleFlagEdit = (item, flag) => {
    setSelectedRotation(item);
    setSelectedFlag(flag);
    setFlagEditModalOpen(true);
  };

  const handleFlagDelete = async () => {
    try {
      const response = await fetch(
        `http://localhost:8000/api/v1/rotations/${selectedRotation.id}/flags/${selectedFlag}`,
        { method: 'DELETE' }
      );

      if (!response.ok) {
        throw new Error('Failed to delete flag');
      }

      // Update local state
      setAllData(allData.map(item => {
        if (item.type === 'rotation' && item.id === selectedRotation.id) {
          return {
            ...item,
            flags: item.flags.filter(f => f !== selectedFlag)
          };
        }
        return item;
      }));

      setFlagEditModalOpen(false);
      console.log(`✅ Deleted ${selectedFlag} flag from rotation ${selectedRotation.identifier}`);
    } catch (error) {
      console.error('Error deleting flag:', error);
      alert(`Failed to delete flag: ${error.message}`);
    }
  };

  const handleFlagChange = async (newFlagId) => {
    try {
      // Delete old flag
      await fetch(
        `http://localhost:8000/api/v1/rotations/${selectedRotation.id}/flags/${selectedFlag}`,
        { method: 'DELETE' }
      );

      // Add new flag
      const response = await fetch(`http://localhost:8000/api/v1/rotations/${selectedRotation.id}/flags`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ flag_type: newFlagId })
      });

      if (!response.ok) {
        throw new Error('Failed to change flag');
      }

      // Update local state
      setAllData(allData.map(item => {
        if (item.type === 'rotation' && item.id === selectedRotation.id) {
          return {
            ...item,
            flags: [...item.flags.filter(f => f !== selectedFlag), newFlagId]
          };
        }
        return item;
      }));

      setFlagEditModalOpen(false);
      console.log(`✅ Changed flag from ${selectedFlag} to ${newFlagId}`);
    } catch (error) {
      console.error('Error changing flag:', error);
      alert(`Failed to change flag: ${error.message}`);
    }
  };

  const hasActiveFilters = filters?.startDate || filters?.endDate || filters?.status !== 'all' || filters?.documentType !== 'all';

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/dashboard', icon: 'Home' },
    { label: 'Data Management', icon: 'Database' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      <div className="lg:ml-60 min-h-screen">
        <div className="p-4 md:p-6 lg:p-8 pb-20 lg:pb-8">
          <div className="mb-6 md:mb-8">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 md:mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
                Data Management
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                Browse and manage rotations and timecards in one unified view
              </p>
            </div>

            <Button
              variant="default"
              iconName="Upload"
              iconPosition="left"
              onClick={() => setIsUploadModalOpen(true)}
              className="w-full sm:w-auto"
            >
              Upload Document
            </Button>
          </div>

          <div className="mb-6 md:mb-8">
            <FilterBar
              onFilterChange={handleFilterChange}
              resultsCount={filteredData?.length}
              isLoading={isLoading}
              filters={filters}
            />
          </div>

          {isLoading ? (
            <div className="bg-card rounded-lg border border-accent-cyan/20 p-8">
              <div className="flex items-center justify-center">
                <Icon name="Loader2" className="w-8 h-8 text-primary animate-spin" />
              </div>
            </div>
          ) : filteredData?.length === 0 ? (
            <EmptyState
              hasFilters={hasActiveFilters}
              onClearFilters={handleClearFilters}
              onUploadClick={() => setIsUploadModalOpen(true)}
            />
          ) : (
            <UnifiedDataTable 
              data={filteredData} 
              onFlagClick={handleFlagClick}
              onDeleteClick={handleDeleteClick}
              onFlagEdit={handleFlagEdit}
            />
          )}
        </div>
      </div>
      
      {/* Floating Action Button */}
      <button
        onClick={() => setIsUploadModalOpen(true)}
        className="fixed bottom-20 lg:bottom-8 right-4 lg:right-8 w-14 h-14 lg:w-16 lg:h-16 rounded-full bg-accent-cyan hover:bg-accent-blue transition-smooth shadow-lg flex items-center justify-center z-50"
        style={{ boxShadow: '0 0 30px rgba(0, 212, 255, 0.6)' }}
        aria-label="Upload document"
      >
        <Icon name="Plus" size={24} color="var(--color-background)" />
      </button>
      
      <UnifiedUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUploadSuccess={handleUploadSuccess}
      />
      
      <FlagModal
        isOpen={flagModalOpen}
        onClose={() => setFlagModalOpen(false)}
        rotation={selectedRotation}
        onConfirm={handleFlagConfirm}
      />

      {/* Delete Confirmation Modal */}
      {deleteConfirmOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg border border-accent-cyan/20 p-6 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <Icon name="AlertTriangle" className="w-6 h-6 text-error" />
              <h3 className="text-xl font-bold text-foreground">
                {deleteStep === 1 && 'Delete Confirmation'}
                {deleteStep === 2 && 'Are You Sure?'}
                {deleteStep === 3 && 'Final Confirmation'}
              </h3>
            </div>
            
            <p className="text-muted-foreground mb-6">
              {deleteStep === 1 && `Delete ${itemToDelete?.type} "${itemToDelete?.identifier}"?`}
              {deleteStep === 2 && 'This action cannot be undone. Are you absolutely sure?'}
              {deleteStep === 3 && 'Last chance! Click "Yes, Delete" to permanently delete this item.'}
            </p>

            <div className="flex gap-3">
              <Button
                variant="ghost"
                onClick={() => {
                  setDeleteConfirmOpen(false);
                  setDeleteStep(1);
                  setItemToDelete(null);
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="default"
                onClick={handleDeleteConfirm}
                className="flex-1 bg-error hover:bg-error/90"
              >
                {deleteStep < 3 ? 'Continue' : 'Yes, Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Flag Edit Modal */}
      {flagEditModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg border border-accent-cyan/20 p-6 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <Icon name="Tag" className="w-6 h-6 text-accent-cyan" />
              <h3 className="text-xl font-bold text-foreground">Edit Flag</h3>
            </div>
            
            <p className="text-muted-foreground mb-4">
              Flag: <span className="font-semibold text-foreground">{selectedFlag?.toUpperCase()}</span> on rotation {selectedRotation?.identifier}
            </p>

            <div className="space-y-3 mb-6">
              <select
                className="w-full px-4 py-2 bg-background border border-accent-cyan/20 rounded-lg text-foreground"
                onChange={(e) => handleFlagChange(e.target.value)}
                defaultValue=""
              >
                <option value="" disabled>Change flag to...</option>
                <option value="reroute">Reroute</option>
                <option value="sick">Sick</option>
                <option value="apd_pd">APD/PD</option>
                <option value="23k">23K</option>
                <option value="ws">WS</option>
                <option value="gs">GS</option>
                <option value="sl">SL</option>
                <option value="qs">QS</option>
                <option value="vac">VAC</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="flex gap-3">
              <Button
                variant="ghost"
                onClick={() => setFlagEditModalOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="default"
                onClick={handleFlagDelete}
                className="flex-1 bg-error hover:bg-error/90"
                iconName="Trash2"
                iconPosition="left"
              >
                Delete Flag
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataManagement;
